import React, { useEffect, useMemo, useState } from "react";
import "./AdminMaintenancePage.css";
import { apiFetch } from "../../../../apiFetch";

const DEFAULT_MESSAGE =
    "We’re doing some updates to improve the platform. Please check back shortly.";

const AdminMaintenancePage = () => {
    const [enabled, setEnabled] = useState(false);
    const [message, setMessage] = useState("");
    const [scheduled, setScheduled] = useState(""); // datetime-local string (local time)
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [status, setStatus] = useState({ type: "", text: "" }); // {type: "success"|"error"|"", text:""}
    const [scheduleConfirm, setScheduleConfirm] = useState(""); // schedule confirmation message

    // ✅ Notify users toggle (UI-only, still sent to backend on save)
    const [notifyUsers, setNotifyUsers] = useState(true);

    // Keep originals so we can disable Save when unchanged
    const [initial, setInitial] = useState({
        enabled: false,
        message: "",
        scheduled: "",
    });

    useEffect(() => {
        const loadSettings = async () => {
            try {
                setStatus({ type: "", text: "" });
                setScheduleConfirm("");

                const res = await apiFetch("/maintenance", { method: "GET" });
                if (!res.ok) throw new Error("Failed to load settings");

                const data = await res.json();

                const nextEnabled = Boolean(data.maintenanceMode);
                const nextMessage = data.maintenanceMessage || "";
                const nextScheduled = data.maintenanceSchedule
                    ? toDatetimeLocal(data.maintenanceSchedule)
                    : "";

                setEnabled(nextEnabled);
                setMessage(nextMessage);
                setScheduled(nextScheduled);

                // ✅ UI-only toggle defaults to ON
                setNotifyUsers(true);

                setInitial({
                    enabled: nextEnabled,
                    message: nextMessage,
                    scheduled: nextScheduled,
                });

                if (data.updatedAt) setLastSavedAt(data.updatedAt);

                if (data.maintenanceSchedule) {
                    const local = new Date(data.maintenanceSchedule).toLocaleString();
                    setScheduleConfirm(
                        `Scheduled maintenance is set for ${local} (your local time).`
                    );
                }
            } catch (err) {
                console.error("Maintenance load error:", err);
                setStatus({ type: "error", text: "Failed to load maintenance settings." });
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    const toggleMaintenance = () => setEnabled((prev) => !prev);

    const saveDisabled = useMemo(() => {
        const msgNow = (message ?? "").trim();
        const msgInit = (initial.message ?? "").trim();
        const schedNow = (scheduled ?? "").trim();
        const schedInit = (initial.scheduled ?? "").trim();

        // ✅ notifyUsers is UI-only; don’t block save based on it
        return (
            saving || (enabled === initial.enabled && msgNow === msgInit && schedNow === schedInit)
        );
    }, [saving, enabled, message, scheduled, initial]);

    const saveSettings = async () => {
        setSaving(true);
        setStatus({ type: "", text: "" });
        setScheduleConfirm("");

        try {
            // datetime-local returns "YYYY-MM-DDTHH:mm" (NO timezone)
            // Convert to ISO UTC string so the backend stores a reliable timestamp
            const scheduleISO = scheduled.trim() ? new Date(scheduled).toISOString() : null;

            const res = await apiFetch("/api/admin/maintenance", {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    maintenanceMode: enabled,
                    maintenanceMessage: message,
                    maintenanceSchedule: scheduleISO, // ISO UTC or null
                    notifyUsers: Boolean(notifyUsers), // ✅ UI-only toggle sent to backend
                }),
            });

            if (!res.ok) {
                const t = await res.text().catch(() => "");
                throw new Error(t || "Failed to save");
            }

            const data = await res.json().catch(() => ({}));

            // Reflect saved values back into UI (and normalize schedule from backend)
            const savedEnabled =
                typeof data.maintenanceMode === "boolean" ? data.maintenanceMode : enabled;

            const savedMessage =
                typeof data.maintenanceMessage === "string" ? data.maintenanceMessage : message;

            const savedScheduled = data.maintenanceSchedule
                ? toDatetimeLocal(data.maintenanceSchedule)
                : "";

            setEnabled(Boolean(savedEnabled));
            setMessage(savedMessage || "");
            setScheduled(savedScheduled);

            setInitial({
                enabled: Boolean(savedEnabled),
                message: savedMessage || "",
                scheduled: savedScheduled,
            });

            if (data.updatedAt) setLastSavedAt(data.updatedAt);

            // Confirmation message for schedule
            if (data.maintenanceSchedule) {
                const local = new Date(data.maintenanceSchedule).toLocaleString();
                setScheduleConfirm(`✅ Maintenance scheduled for ${local} (your local time).`);
            } else if (scheduleISO) {
                const local = new Date(scheduleISO).toLocaleString();
                setScheduleConfirm(`✅ Maintenance scheduled for ${local} (your local time).`);
            } else {
                setScheduleConfirm("✅ No schedule set.");
            }

            // Notification confirmation from backend
            if (data.notified === true) {
                const sentCount = typeof data.sentCount === "number" ? data.sentCount : null;
                setStatus({
                    type: "success",
                    text: sentCount
                        ? `Maintenance settings updated. ✅ Users notified (${sentCount}).`
                        : "Maintenance settings updated. ✅ Users notified.",
                });
            } else {
                setStatus({ type: "success", text: "Maintenance settings updated." });
            }
        } catch (err) {
            console.error(err);
            setStatus({ type: "error", text: "Failed to update maintenance settings." });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="maintenance-admin-wrapper">
                <div className="maintenance-admin-card">
                    <h2>Loading settings...</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="maintenance-admin-wrapper">
            <div className="maintenance-admin-card">
                <h1>Maintenance Control</h1>

                <div className="status-row">
                    <span>Status</span>

                    <label className="switch">
                        <input type="checkbox" checked={enabled} onChange={toggleMaintenance} />
                        <span className="slider" />
                    </label>

                    <span className={enabled ? "status-on" : "status-off"}>
                        {enabled ? "ACTIVE" : "OFF"}
                    </span>
                </div>

                <div className="section">
                    <label>Maintenance Message</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={DEFAULT_MESSAGE}
                    />
                </div>

                <div className="section">
                    <label>Schedule Time (optional)</label>
                    <input
                        type="datetime-local"
                        value={scheduled}
                        onChange={(e) => setScheduled(e.target.value)}
                    />
                    <small style={{ display: "block", marginTop: 6, opacity: 0.8 }}>
                        Schedule uses your local time. It’s saved as UTC on the server.
                    </small>

                    {scheduleConfirm ? (
                        <div className="inline-success" style={{ marginTop: 10 }}>
                            {scheduleConfirm}
                        </div>
                    ) : null}
                </div>

                {/* ✅ Notify users (UI-only toggle) */}
                <div className="section" style={{ marginTop: 8 }}>
                    <div className="notify-row">
                        <input
                            type="checkbox"
                            checked={notifyUsers}
                            onChange={(e) => setNotifyUsers(e.target.checked)}
                        />
                        <div className="notify-text">
                            <div className="notify-title">
                                Notify users about maintenance changes (recommended)
                            </div>
                            <div className="notify-desc">
                                When ON, saving these settings notifies users (schedule/started/ended).
                            </div>
                        </div>
                    </div>
                </div>

                {lastSavedAt ? (
                    <div style={{ marginTop: 8, opacity: 0.85, fontSize: 12 }}>
                        Last saved: {formatTimestamp(lastSavedAt)}
                    </div>
                ) : null}

                {status.text ? (
                    <div
                        className={status.type === "success" ? "inline-success" : "inline-error"}
                        style={{ marginTop: 10 }}
                    >
                        {status.text}
                    </div>
                ) : null}

                <button className="save-btn" onClick={saveSettings} disabled={saveDisabled}>
                    {saving ? "Saving..." : saveDisabled ? "No changes" : "Save Settings"}
                </button>

                <div className="preview">
                    <h3>Preview</h3>
                    <p>{(message || "").trim() || DEFAULT_MESSAGE}</p>
                </div>
            </div>
        </div>
    );
};

// ISO -> "YYYY-MM-DDTHH:mm" for datetime-local (local time)
function toDatetimeLocal(isoString) {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
}

function formatTimestamp(value) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
}

export default AdminMaintenancePage;









